import { get, pick, pickBy, isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { pixelReservationEvent } from '../instantExperiences/actions';
import { setWaitingListInfo } from '../waitingList/actions';
import {
  ADD_CUSTOMER_TO_WAITING_LIST,
  ADD_FIELD,
  CANCEL_RESERVATION,
  CHECK_TIME_AVAILABLE,
  CLEAR_RESERVATIONS,
  CREATE_RESERVATION,
  FAILED_CHARGES,
  FETCH_FEE,
  FETCH_PROMO_CODE,
  INVALID_PROMO_CODE,
  PROCESS_PAYMENT,
  PROCESS_PAYMENT_SUCCESS,
  RECEIVE_RESERVATION,
  REQUEST_RESERVATION,
  RESET_FEE,
  RESET_FIELDS,
  RESET_PROCESS_PAYMENT,
  RESET_PROMO_CODE,
  RESET_TIP,
  SET_GIFT_CARD_CODE,
  SET_PROCESSING,
  SET_TIP,
  SET_TIPS,
  SET_CUSTOM_TIP,
  SET_PROMO_CODE,
  SET_TOTAL,
  SET_REDIRECTION,
  TIME_AVAILABLE,
  TIME_UNAVAILABLE,
  TOUCH_FIELD,
  UPDATE_FIELD,
  VALIDATE_FIELD,
  FAILED_BOOK_AND_RESERVE,
  SET_TAX_BREAKDOWN,
  RESET_PROMO_ERROR,
  REMOVE_PROMO_FROM_RESERVATION,
  SET_SERVICES,
  SET_TOTAL_WITHOUT_TIP_AND_FEES,
  ANY_BARBER,
  VALIDATING_APPT_REQUEST_STARTED,
  VALIDATING_APPT_REQUEST_FINISHED,
  SET_MEMBERSHIP_DISCOUNTS,
} from '../constants';
import { applePaySession, mapApplePayUser } from '../utils/applePay';
import {
  checkStatus,
  handleRequestError,
  paramMissing,
  joinQueryStringParams,
} from '../utils/helpers';
import config from '../../config';
import googleAnalytics from '../applicationServices/analytics/googleAnalytics';
import analytics
  from '../applicationServices/analytics/BookingAnalytics';

const PAYMENT_REQUIRED = 'PaymentRequired';

export const setProcessing = payload => ({ type: SET_PROCESSING, payload });

export const wrapWithSetProcessingAction = action => (...args) => async (dispatch) => {
  dispatch(setProcessing(true));
  try {
    await dispatch(action(...args));
  } catch (e) {
    /* Error is already handled in action */
  }
  dispatch(setProcessing(false)); // Finally
};

const cancelAppointmentAction = appointmentId => (dispatch) => {
  const url = `${config.url}/cancel-appointment/${appointmentId}`;

  dispatch({
    type: CANCEL_RESERVATION,
    payload: appointmentId,
  });

  return fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      platform: 'Web',
      sendNotifications: true,
    }),
  })
    .then(checkStatus)
    .then(() => dispatch({
      type: SET_REDIRECTION,
      payload: `/appointment-canceled/${appointmentId}`,
    }))
    .catch(handleRequestError(dispatch));
};
export const cancelAppointment = wrapWithSetProcessingAction(cancelAppointmentAction);

export const resetTip = () => ({ type: RESET_TIP });

export const setTip = tip => ({
  type: SET_TIP,
  payload: tip,
});

export const resetFields = () => ({ type: RESET_FIELDS });

const addField = field => ({
  type: ADD_FIELD,
  payload: field,
});

export const addAdultsOnlyField = (shopName = 'Barbershop') => {
  const field = {
    id: 'adultsonly',
    name: 'adults-only',
    type: 'checkbox',
    label: `Are you over 21 years old? ${shopName} serves liquor and only allows adults on premises.`,
    fullWidth: true,
    errorMessage: 'You must verify that you are over 21 years old.',
    required: true,
  };

  return addField(field);
};

const validateField = (id, countryCode, shop) => ({
  type: VALIDATE_FIELD,
  payload: { id, countryCode, shop },
});

export const touchField = (id, countryCode, shop) => (dispatch) => {
  dispatch({
    type: TOUCH_FIELD,
    payload: id,
  });

  return dispatch(validateField(id, countryCode, shop));
};

export const updateField = (id, value, countryCode, shop) => (dispatch) => {
  dispatch({
    type: UPDATE_FIELD,
    payload: { id, value },
  });

  return dispatch(validateField(id, countryCode, shop));
};

export const resetPaymentProcessing = () => ({ type: RESET_PROCESS_PAYMENT });

export const processPayment = () => ({ type: PROCESS_PAYMENT });

export const checkForFailedCharges = dispatch => ({ code, message, kind }) => {
  if (code === PAYMENT_REQUIRED) {
    return dispatch({
      type: FAILED_CHARGES,
      payload: message,
    });
  }

  return Promise.reject({ code, message, kind });
};

export const processPaymentSuccess = () => ({ type: PROCESS_PAYMENT_SUCCESS });


const saveConsents = (customerId, shopId, smsConsent, emailConsent) => {
  const url = `${config.url}/customer/${customerId}/shop/${shopId}/notifications-consent`;
  const payload = {
    promotional: {
      phone: !!smsConsent,
      email: !!emailConsent,
    },
  };

  fetch(url, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
};

const callCreateReservationAPIAction = (
  reservationUrl,
  reservationData,
  baseUrl = '/view-reservation',
  consents,
  turnstileToken,
) => (dispatch) => {
  const turnstileHeaders = turnstileToken ? { 'cf-turnstile-response': turnstileToken } : {};

  return fetch(reservationUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...turnstileHeaders,
    },
    body: JSON.stringify(reservationData),
  })
    .then(checkStatus)
    .then((data) => {
      if (!data) {
        return false;
      }

      dispatch({
        type: SET_REDIRECTION,
        payload: `${baseUrl}/${data.id}`,
      });

      dispatch(pixelReservationEvent(data));

      analytics.bookingComplete({
        totalWithTip: data.totalWithTip,
        appointmentId: data.id,
      });
      analytics.identify(data.customerID, {
        firstName: data.customer.firstName,
        lastName: data.customer.lastName,
        email: data.customer.email,
        phone: data.customer.phone,
      });

      if (consents) {
        saveConsents(data.customerId, data.shopId, consents.smsConsent, consents.emailConsent);
      }

      return dispatch(processPaymentSuccess());
    })
    .catch(checkForFailedCharges(dispatch))
    .catch((error) => {
      analytics.paymentError(error);
      handleRequestError(dispatch)(error);
    });
};
const callCreateReservationAPI = wrapWithSetProcessingAction(callCreateReservationAPIAction);

export const createReservation = ({
  redirectUrl,
  serviceIds,
  reservationTime,
  barberId,
  user,
  tipAmount,
  token,
  bookNoPay,
  anyBarber,
  promoCode,
  isReservation,
  kiosk,
  thirdPartyPromotions,
  consents = null,
  turnstileToken,
}) => (dispatch) => {
  const dateTime = moment(reservationTime, config.timeSlugFormat);

  const reservationData = {
    barberId,
    customer: user,
    dateTime: dateTime.format(),
    appointment_service: serviceIds.split(';').map(serviceId => ({ serviceId })),
    tipAmount,
    sendNotifications: true,
    platform: 'Web',
    bookedWithAnyBarber: anyBarber,
    promoCode,
    isReservation,
    bookNoPay,
    thirdPartyPromotions,
  };
  if (token) {
    reservationData.paymentToken = token.id;
  }

  dispatch({ type: CREATE_RESERVATION, payload: reservationData });

  const params = joinQueryStringParams([kiosk ? 'kiosk=true' : '']);
  const reservationUrl = `${config.url}/book-appointment${params}`;
  return dispatch(
    callCreateReservationAPI(reservationUrl, pickBy(reservationData), redirectUrl, consents, turnstileToken)
  );
};

const requestReservation = id => ({
  type: REQUEST_RESERVATION,
  payload: id,
});

export const receiveReservation = reservation => ({
  type: RECEIVE_RESERVATION,
  payload: reservation,
});

const shouldFetchReservation = (state, id) => (state.reservation.getIn(['appointment', 'id']) !== id);

export const getAppointmentDetails = id => (
  fetch(`${config.url}/view-appointment/${id}`).then(checkStatus)
);

export const getBrandReservation = (brandId, confirmationCode) => (
  fetch(`${config.url}/brand/${brandId}/reservation/${confirmationCode}`).then(checkStatus)
);

export const getReservation = confirmationCode => (
  fetch(`${config.url}/reservation/${confirmationCode}`).then(checkStatus)
);

export const getBrandRewardProgram = brandId => (
  fetch(`${config.url}/brand/${brandId}/reward`).then(checkStatus)
);

export const getCustomerReward = customerRewardId => (
  fetch(`${config.url}/customer-reward/${customerRewardId}`).then(checkStatus)
);

export function fetchReservation(appointmentId) {
  return (dispatch, getState) => {
    if (!shouldFetchReservation(getState(), appointmentId)) return Promise.resolve();

    dispatch(requestReservation(appointmentId));

    return getAppointmentDetails(appointmentId)
      .then((appointment) => {
        dispatch(receiveReservation(appointment));

        const { customer } = appointment;

        analytics.identify(customer.customerID, {
          firstName: customer.firstName,
          lastName: customer.lastName,
          email: customer.email,
          phone: customer.phone,
        });
      })
      .catch(handleRequestError(dispatch));
  };
}

export const clearReservations = () => ({
  type: CLEAR_RESERVATIONS,
});

export const applePay = ({
  redirectUrl,
  serviceIds,
  reservationTime,
  barberId,
  tipAmount,
  bookNoPay,
  anyBarber,
  amount,
  promoCode,
  isReservation,
  currency,
  thirdPartyPromotions,
  consents,
}) => dispatch => applePaySession(
  amount,
  currency,
  result => dispatch(createReservation({
    redirectUrl,
    serviceIds,
    reservationTime,
    barberId,
    user: mapApplePayUser(get(result, 'shippingContact', {})),
    tipAmount,
    token: result.token,
    bookNoPay,
    anyBarber,
    promoCode,
    isReservation,
    thirdPartyPromotions,
    consents,
  })),
  (error) => {
    analytics.paymentError(error);
    handleRequestError(dispatch)(error);
  }
);

export const resetPromo = () => ({
  type: RESET_PROMO_CODE,
});

export const removePromoFromReservation = () => ({
  type: REMOVE_PROMO_FROM_RESERVATION,
});

export const resetPromoError = () => dispatch => dispatch({ type: RESET_PROMO_ERROR });

const startFetchPromo = () => ({
  type: FETCH_PROMO_CODE,
});

const invalidPromo = error => ({
  type: INVALID_PROMO_CODE,
  payload: error,
});

const setPromo = (promo, promoCode) => ({
  type: SET_PROMO_CODE,
  payload: {
    amount: promo.promoAmount,
    validationNote: promo.validationNote,
    promoCode,
  },
});

const setGiftCard = (giftCard, promoCode) => ({
  type: SET_GIFT_CARD_CODE,
  payload: {
    appliedAmount: giftCard.appliedAmount,
    remainingAmount: giftCard.remainingAmount,
    promoCode,
  },
});

const setPromoCode = promoCode => ({
  type: SET_PROMO_CODE,
  payload: { promoCode },
});

export const fetchPromo = promoCode => (dispatch) => {
  dispatch(startFetchPromo());
  dispatch(setPromoCode(promoCode));
};

export const resetFee = () => ({ type: RESET_FEE });

const setTaxBreakdown = breakdown => ({
  type: SET_TAX_BREAKDOWN,
  payload: breakdown,
});

export const appointmentValidate = (params, giftCardEnabled) => (dispatch) => {
  const requiredKeys = ['shopId', 'barberId', 'services'];
  const requestId = Math.random();
  if (paramMissing(requiredKeys, params) || params.tip === undefined) return null;

  const paramKeys = [
    ...requiredKeys,
    'dateTime',
    'firstName',
    'lastName',
    'phone',
    'email',
    'promoCode',
    'includeWaitingListFee',
    'tipAmount',
    'tip',
    'services',
    'duration',
    'appointmentId',
  ];
  if (params.tip === null && !params.useFlatTips) {
    paramKeys.splice(paramKeys.indexOf('tipAmount'), 1);
  }
  const url = `${config.url}/appointment/validate`;

  const body = pick(params, paramKeys);
  if (body.barberId === ANY_BARBER) {
    delete body.barberId;
  }

  dispatch({ type: VALIDATING_APPT_REQUEST_STARTED, payload: requestId });

  return fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
    .then(checkStatus)
    .then((response) => {
      const {
        fees,
        promo = {},
        giftCard = {},
        taxBreakdown = [],
        tips = [],
        tip = null,
        total = 0,
        totalWithoutTipAndFees = 0,
        services = [],
        validTime = true,
        membershipDiscounts = [],
      } = response;

      dispatch({ type: VALIDATING_APPT_REQUEST_FINISHED, payload: requestId });

      if (!params.appointmentId) {
        dispatch({ type: CHECK_TIME_AVAILABLE });

        if (validTime) {
          dispatch({ type: TIME_AVAILABLE });
        } else {
          dispatch({
            type: TIME_UNAVAILABLE,
            payload: {
              message: 'The selected time is no longer available. ' +
                'Please try another time or call for assistance. Thank you for your Service.',
              redirect: params.fallbackUrl,
            },
          });
        }
      }

      dispatch({
        type: SET_SERVICES,
        payload: services,
      });

      dispatch({
        type: SET_TIPS,
        payload: tips,
      });

      // this indicates that default tip was not set and needs to be consumed from the validation result
      const needToSetDefaultTip = params.tip === null && !params.tipAmount;
      if (needToSetDefaultTip) {
        if (tip && !tip.flatAmount) {
          dispatch(setTip(tip.percentage));
        } else if (tip && tip.flatAmount) {
          dispatch(setTip(tip.flatAmount));
        } else {
          dispatch(setTip(null));
        }
      }

      dispatch({
        type: FETCH_FEE,
        payload: fees.customerBookingFee,
      });

      dispatch(setTaxBreakdown(taxBreakdown));

      dispatch({
        type: SET_TOTAL,
        payload: total,
      });

      dispatch({
        type: SET_TOTAL_WITHOUT_TIP_AND_FEES,
        payload: totalWithoutTipAndFees,
      });

      dispatch({
        type: SET_MEMBERSHIP_DISCOUNTS,
        payload: membershipDiscounts,
      });

      if (!params.promoCode) return;

      if (giftCardEnabled && giftCard.error) {
        dispatch(invalidPromo(giftCard.error));
        return;
      }
      if (giftCardEnabled && !isEmpty(giftCard)) {
        dispatch(setGiftCard(giftCard, params.promoCode));
        return;
      }

      if (promo.error) {
        dispatch(invalidPromo(promo.error));
        return;
      }
      if (!isEmpty(promo)) {
        dispatch(setPromo(promo, params.promoCode));
        return;
      }

      dispatch(invalidPromo('Invalid promo code'));
    })
    .catch((e) => {
      dispatch({ type: VALIDATING_APPT_REQUEST_FINISHED, payload: requestId });
      handleRequestError(dispatch(e));
    });
};

export const addCustomerToWaitingList = ({
  shopId,
  barberId,
  serviceIds,
  day,
  user,
  token,
  isApplePay,
  redirectUrl,
  tipAmount,
  consents,
}) => (dispatch) => {
  const { firstName, lastName, phone, email } = user;
  const url = `${config.url}/customer/waiting-list`;
  return fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      shopId,
      barberId,
      serviceIds,
      day,
      firstName,
      lastName,
      phone,
      email,
      paymentToken: token,
      isApplePay,
      tipAmount,
    }),
  }).then(checkStatus)
    .then((response) => {
      dispatch({ type: ADD_CUSTOMER_TO_WAITING_LIST });
      dispatch(setWaitingListInfo(shopId, barberId, serviceIds, response.numberInLine));

      if (consents) {
        saveConsents(response.customerId, shopId, consents.smsConsent, consents.emailConsent);
      }

      return dispatch({ type: SET_REDIRECTION, payload: redirectUrl });
    })
    .catch(handleRequestError(dispatch));
};

export const applePayAddCustomerToWaitingList = (amount, currency, params) => dispatch => applePaySession(
  amount,
  currency,
  result => dispatch(addCustomerToWaitingList({
    ...params,
    isApplePay: true,
    token: result.token.id,
    user: mapApplePayUser(get(result, 'shippingContact', {})),
  })),
  handleRequestError(dispatch)
);

export const setCustomTip = amount => ({
  type: SET_CUSTOM_TIP,
  payload: amount,
});

const rescheduleAction = (
  appointmentId, dateTime, barberId, removeInvalidPromo, turnstileToken
) => (dispatch) => {
  dispatch(setProcessing(true));

  const turnstileHeaders = turnstileToken ? { 'cf-turnstile-response': turnstileToken } : {};

  return fetch(
    `${config.url}/reschedule-appointment/${appointmentId}`,
    {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...turnstileHeaders,
      },
      body: JSON.stringify({
        dateTime,
        barberId,
        confirmations: {
          removeInvalidPromo,
        },
      }),
    }
  )
    .then(checkStatus)
    .then((appointment) => {
      if (!appointment) { return false; }
      dispatch({
        type: SET_REDIRECTION,
        payload: `/view-reservation/${appointment.id}`,
      });
      dispatch(setProcessing(false));
      return dispatch(receiveReservation(appointment));
    })
    .catch(handleRequestError(dispatch));
};
export const reschedule = wrapWithSetProcessingAction(rescheduleAction);

export const validatePrepayOnlyCustomer = dispatch => (params) => {
  const paramKeys = [
    'shopId',
    'barberId',
    'serviceCost',
    'dateTime',
    'firstName',
    'phone',
    'email',
    'promoCode',
    'includeWaitingListFee',
    'tipAmount',
    'validatePrepayOnly',
    'services',
  ];
  const url = `${config.url}/appointment/validate`;

  return fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(pick(params, paramKeys)),
  })
    .then(checkStatus)
    .then(({ customer }) => {
      if (customer && customer.error) {
        dispatch({
          type: FAILED_BOOK_AND_RESERVE,
          payload: customer.error,
        });

        return Promise.reject();
      }

      return { customer };
    })
    .catch(handleRequestError(dispatch));
};

const reserveTimeAction = (barberId, action, dateTime = null, duration = 0, kiosk = false) => {
  const kioskParam = kiosk ? '?kiosk=true' : '';
  const url = `${config.url}/barber/${barberId}/reserve-time${kioskParam}`;
  return fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ dateTime, action, duration }),
  }).then(checkStatus);
};

export const reserveTime = (barberId, dateTime, duration, kiosk) =>
  reserveTimeAction(barberId, 'add', dateTime, duration, kiosk);
export const removeReservedTime = barberId => reserveTimeAction(barberId, 'remove');

const chargeAppointmentAction = (id, data) => (dispatch) => {
  const url = `${config.url}/charge-appointment/${id}`;

  return fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
    .then(checkStatus)
    .then((response) => {
      if (!response) {
        return false;
      }

      googleAnalytics.event({
        category: 'Booking',
        action: 'Charge from link',
        value: get(response, 'payment.customerBookingFee', 0),
      });

      dispatch({
        type: SET_REDIRECTION,
        payload: `/view-reservation/${response.id}`,
      });
      return dispatch(processPaymentSuccess());
    })
    .catch(handleRequestError(dispatch));
};

export const chargeAppointment = wrapWithSetProcessingAction(chargeAppointmentAction);

export const fetchThirdPartyPromoCode = (customerID, subjectID) => (
  fetch(`${config.url}/promotions/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      subjectType: 'appointment',
      customerId: customerID,
      subjectId: subjectID,
    }),
  })
    .then(response => response.json())
);



// WEBPACK FOOTER //
// ./src/app/reservation/actions.js